import { Ban, Moon, ShieldCheck } from 'lucide-react'
import type { Book } from '../types'
import './DormancyPanel.css'

interface DormancyPanelProps {
  book: Book
}

type DormancyState = 'dormant' | 'halted' | 'running'

/**
 * State-precedence readout for the live book's containment: dormant (not
 * built as an engine book at all), halted (built but gated off with a
 * reason), or running. Explains the double-gate so a stray registry row
 * alone reads as harmless, not alarming.
 */
export function DormancyPanel({ book }: DormancyPanelProps) {
  const status = book.bookStatus

  let state: DormancyState
  if (status === null) {
    state = 'dormant'
  } else if (status.halted === true) {
    state = 'halted'
  } else {
    state = 'running'
  }

  const Icon = state === 'running' ? ShieldCheck : state === 'halted' ? Ban : Moon

  return (
    <div className="card dormancy-panel">
      <div className="card-header">
        <h3 className="card-title">
          <Icon size={18} className="icon" aria-hidden="true" />
          Containment state
        </h3>
      </div>

      {state === 'dormant' && (
        <div className="dormancy-panel-headline dormancy-panel-headline--dormant">
          <div className="dormancy-panel-title">DORMANT · built-but-OFF</div>
          <p className="dormancy-panel-sub">
            account exists in the registry but is not enabled; not built as an engine book.
          </p>
        </div>
      )}

      {state === 'halted' && (
        <div className="dormancy-panel-headline dormancy-panel-headline--halted">
          <div className="dormancy-panel-title">HALTED</div>
          <p className="dormancy-panel-sub">
            {status?.halt_reason ?? 'halted (no reason reported)'}
          </p>
        </div>
      )}

      {state === 'running' && (
        <div className="dormancy-panel-headline dormancy-panel-headline--running">
          <div className="dormancy-panel-title">RUNNING</div>
        </div>
      )}

      <p className="caption dormancy-panel-caption">
        Entry to live requires <strong>both</strong> the deploy-time env gate
        (LIVE_TRADING_ENABLED) <strong>and</strong> a subscription to the research feed —
        a stray row in the registry alone can&apos;t trade.
      </p>
    </div>
  )
}
